import { call, put, select, all } from 'redux-saga/effects';
import history from '../history';
import moment from 'moment';
import { setActiveIncidentResponse } from '../actions/incidents';
import { createReportResponse } from '../actions/reports';
import { setActiveSafetyWalk } from '../actions/safetyWalks';
import {
  addUserTaskResponse,
  deleteUserTaskResponse,
  setActiveTaskResponse,
  updateRemindedAssignments
} from '../actions/tasks';
import {
  getActiveCompany,
  getActiveLocationId,
  getActiveProject
} from '../selectors/company';
import { getActiveSafetyWalkSelector } from '../selectors/safetyWalks';
import { addUploadedAttachmentsResponse } from '../actions/attachments';
import { getAddedAttachmentsSelector } from '../selectors/attachments';
import customToast from '../utils/customToast';

export function* loadTask(apiV4, { payload }) {
  try {
    let task;

    if (payload.fromRepeating) {
      const repeatable = yield call(apiV4.fetchTaskRepeatableById, payload.id);
      task = {
        ...repeatable.task,
        schedule: 'repeating',
        assignedTo: repeatable.assignedTo,
        dueBy: repeatable.dueBy,
        dueByTime: repeatable.dueByTime,
        dueByNumber: repeatable.dueByNumber,
        frequency: repeatable.frequency,
        companyId: repeatable.companyId,
        groupId: repeatable.groupId,
        projectId: repeatable.projectId,
        repeatingOn: repeatable.repeatingOn,
        createdAt: repeatable.createdAt,
        createdBy: repeatable.createdBy,
        _id: repeatable._id,
        type: repeatable.type,
        description: repeatable.description,
        fromRepeating: true,
        isPaused: repeatable.isPaused
      };
    } else {
      task = yield call(apiV4.fetchTaskById, payload);

      let attachments =
        task?.attachments?.length > 0
          ? yield call(apiV4.fetchAttachmentsByIds, task.attachments)
          : [];

      yield put(addUploadedAttachmentsResponse(attachments));

      const comments = yield call(apiV4.fetchTaskComments, task._id);

      task = { ...task, comments, schedule: 'immediate' };
    }

    if (task) {
      yield put(setActiveTaskResponse(task));
      task?.type === 'correctiveAction'
        ? history.push('/app/correctiveActionContainer')
        : history.push('/app/taskContainer');
    }
  } catch (e) {
    console.log(e);
  }
}

export function* sendReminder(
  apiV4,
  { payload: { assignmentId, assignmentType } }
) {
  try {
    let today = new Date();
    let current = JSON.parse(localStorage.getItem('remindersSent'));

    localStorage.setItem(
      'remindersSent',
      JSON.stringify({ ...current, [assignmentId]: today })
    );
    let updated = JSON.parse(localStorage.getItem('remindersSent'));
    const result = yield call(apiV4.sendReminder, assignmentId, assignmentType);
    yield put(updateRemindedAssignments({ ...updated }));

    if (result.failedRecipients.length > 0) {
      const failedNames = result.failedRecipients
        .join(`, `)
        .replace(/, ((?:.(?!, ))+)$/, ' and $1');
      const plural =
        result.failedRecipients.length > 1
          ? { verb: `weren't`, ending: `s` }
          : { verb: `wasn't`, ending: `` };
      customToast(
        `${failedNames} ${plural.verb} reminded.
          To send reminders, add an email for the user${plural.ending}
          or have the user${plural.ending} turn on push notifications.`,
        'error'
      );
    } else {
      customToast('Reminder Sent!', 'success');
    }
  } catch (e) {
    console.log(e);
    customToast(e, 'error');
  }
}

export function* setActiveTask(apiV4, { payload }) {
  try {
    yield put(setActiveTaskResponse(payload));
    if (
      payload.ownerType === 'safetyWalks' ||
      payload.ownerType === 'safetyAuditQuestion' ||
      payload.ownerType === 'qualityAuditQuestion' ||
      payload.ownerType === 'lotoAuditQuestion' ||
      payload.ownerType === 'observation' ||
      payload.ownerType === 'safetySuggestion'
    ) {
      const pics = payload.pictures ?? [];
      if (payload?.attachments?.length > 0) {
        const attachments = yield call(
          apiV4.fetchAttachmentsByIds,
          payload?.attachments
        );
        yield put(addUploadedAttachmentsResponse([...pics, ...attachments]));
      } else {
        yield put(addUploadedAttachmentsResponse(pics));
      }
    }
    if (payload.ownerType === 'incident') {
      yield put(addUploadedAttachmentsResponse([]));
    }
    payload.type === 'correctiveAction'
      ? history.push('/app/correctiveActionContainer')
      : history.push('/app/taskContainer');
  } catch (e) {
    console.log(e);
  }
}

export function* addUserTask(apiV4, { payload }) {
  try {
    const activeCompany = yield select(getActiveCompany);
    const activeGroup = yield select(getActiveLocationId);
    const activeProject = yield select(getActiveProject);
    const addedAttachments = yield select(getAddedAttachmentsSelector);

    payload = {
      ...payload,
      groupId: activeGroup?._id ?? payload.groupId,
      projectId: activeProject?._id ?? payload.projectId,
      isCompleted: false,
      dueDate: moment(Date.parse(payload.dueDate)),
      companyId: activeCompany._id
    };

    if (payload.schedule === 'repeating') {
      yield call(apiV4.createTaskRepeatable, payload);
    } else {
      let data = payload?.attachmentData;
      delete payload?.attachmentData;
      let pictures = [];

      if (data) {
        pictures = yield call(apiV4.uploadAttachment, data.data, {
          ownerId: 'pending',
          ownerType: data.ownerType,
          isSignature: false,
          isPhotoEvidence: false,
          photoType: data.photoType
        });
      }

      let together = [...addedAttachments, ...pictures];
      payload = { ...payload, attachments: together };

      let response = yield call(apiV4.addTask, payload);
      response = response?.[0];
      yield put(addUploadedAttachmentsResponse([]));

      if (response.ownerType === 'incident') {
        const incident = yield call(apiV4.fetchIncidentById, response.ownerId);

        const reportComponent = incident.reportComponents.filter(report => {
          if (payload.type === 'correctiveAction') {
            return report[0] === 'Corrective Actions';
          }
          return report[0] === response.reportComponentName;
        });
        yield put(setActiveIncidentResponse(incident));
        yield put(createReportResponse(reportComponent));
      } else if (response.ownerType === 'safetyWalks') {
        const activeSafetyWalk = yield select(getActiveSafetyWalkSelector);
        const items = activeSafetyWalk.items;

        items[`${payload.hazardNumber}`].tasks = [response];
        activeSafetyWalk.items = items;

        if (activeSafetyWalk._id) {
          const swResponse = yield call(
            apiV4.updateSafetyWalk,
            activeSafetyWalk
          );
          let items = swResponse?.bravos
            ?.concat(swResponse?.hazards)
            ?.sort((a, b) => a.overallOrder - b.overallOrder);

          yield put(setActiveSafetyWalk({ ...swResponse, items }));
        } else {
          yield put(setActiveSafetyWalk({ ...activeSafetyWalk }));
        }

        yield put(addUserTaskResponse(response));
      }
      if (response.ownerType === 'equipment') {
        history.push(`/app/equipmentContainer/${response.ownerId}`);
      } else history.goBack();
      customToast('Created Successfully!', 'success');
    }
  } catch (e) {
    customToast(e, 'error');
  }
}

export function* updateUserTask(apiV4, { payload }) {
  try {
    const activeCompany = yield select(getActiveCompany);
    const addedAttachments = yield select(getAddedAttachmentsSelector);
    payload = {
      ...payload,
      companyId: payload.companyId ? payload.companyId : activeCompany._id
    };

    if (payload.schedule === 'repeating') {
      yield call(apiV4.updateTaskRepeatable, payload);
    } else {
      let data = payload?.attachmentData;
      delete payload?.attachmentData;
      let pictures = [];

      if (data) {
        pictures = yield call(apiV4.uploadAttachment, data.data, {
          ownerId: data.ownerId,
          ownerType: data.ownerType,
          isSignature: false,
          isPhotoEvidence: false,
          photoType: data.photoType
        });
      }

      let allAttachments = [...addedAttachments, ...pictures];
      yield put(addUploadedAttachmentsResponse(allAttachments));
      payload = { ...payload, attachments: allAttachments?.map(a => a._id) };
      if ('dueDate' in payload) {
        payload.dueDate = moment(Date.parse(payload.dueDate));
      }

      delete payload.createdBy;

      const response = yield call(apiV4.updateTask, payload);
      const comments = yield call(apiV4.fetchTaskComments, response._id);

      const together = { ...response, schedule: payload.schedule, comments };

      if (response.ownerType === 'incident') {
        const incident = yield call(apiV4.fetchIncidentById, response.ownerId);

        const reportComponent = incident.reportComponents.filter(report => {
          if (payload.type === 'correctiveAction') {
            return report[0] === 'Corrective Actions';
          }
          return report[0] === response.reportComponentName;
        });
        yield put(setActiveIncidentResponse(incident));
        yield put(
          createReportResponse({ report: reportComponent, stage: 'action' })
        );
      } else if (response.ownerType === 'safetyWalks' && response.ownerId) {
        const swResponse = yield call(
          apiV4.fetchSafetyWalkById,
          response.ownerId
        );
        let items = swResponse?.bravos
          ?.concat(swResponse?.hazards)
          ?.sort((a, b) => a.overallOrder - b.overallOrder);
        yield put(setActiveSafetyWalk({ ...swResponse, items }));
      }

      yield put(setActiveTaskResponse({ ...together }));
    }
    customToast(
      `Saved Successfully. ${
        payload.ownerType === 'incident' ? '' : 'Any Photos are Autosaved'
      }`,
      'success'
    );
  } catch (e) {
    customToast(e, 'error');
  }
}

export function* deleteUserTask(apiV4, { payload }) {
  try {
    const addedAttachments = payload.attachments;
    const goBack = payload?.goBack ?? true;

    if (payload.schedule === 'repeating') {
      yield call(apiV4.deleteTaskRepeatable, payload);
    } else {
      let taskPhotos = addedAttachments?.filter(a => a.ownerType === 'task');
      yield all(
        taskPhotos?.map(attachment =>
          call(apiV4.deleteAttachment, attachment._id)
        )
      );

      yield call(apiV4.deleteTask, payload);
      yield put(deleteUserTaskResponse(payload));

      if (payload.ownerType === 'safetyWalks') {
        const activeSafetyWalk = yield select(getActiveSafetyWalkSelector);
        if (activeSafetyWalk._id && payload.ownerId) {
          const index = activeSafetyWalk?.hazards?.findIndex(
            i => i?.tasks?.[0]?._id === payload._id
          );
          activeSafetyWalk.hazards[index].tasks = [];
          const swResponse = yield call(
            apiV4.updateSafetyWalk,
            activeSafetyWalk
          );
          let items = swResponse?.bravos
            ?.concat(swResponse?.hazards)
            ?.sort((a, b) => a.overallOrder - b.overallOrder);
          yield put(setActiveSafetyWalk({ ...swResponse, items: items }));
        } else {
          const index = activeSafetyWalk?.items?.findIndex(
            i => i?.tasks?.[0]?._id === payload._id
          );
          let updated = { ...activeSafetyWalk?.items?.[index], tasks: [] };
          let updatedItems = activeSafetyWalk?.items.toSpliced(
            index,
            1,
            updated
          );
          yield put(
            setActiveSafetyWalk({ ...activeSafetyWalk, items: updatedItems })
          );
        }
      } else if (payload.ownerType === 'incident') {
        const incident = yield call(apiV4.fetchIncidentById, payload.ownerId);

        const reportComponent = incident.reportComponents.filter(report => {
          if (payload.type === 'correctiveAction') {
            return report[0] === 'Corrective Actions';
          }
          return report[0] === payload.reportComponentName;
        });
        yield put(setActiveIncidentResponse(incident));
        yield put(createReportResponse(reportComponent));
      }
    }
    yield put(addUploadedAttachmentsResponse([]));
    customToast('Deleted Successfully!', 'success');
    if (goBack) history.goBack();
    yield;
  } catch (e) {
    customToast(e, 'error');
  }
}
